<template>
  <div>
    <!-- 配置对话框 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="dialogTitle"
      width="600px"
      :close-on-click-modal="false"
      class="form-config-dialog"
    >
      <el-form ref="form" :model="form" label-width="120px" :rules="rules" class="form-content">
        <!-- 标签页容器 -->
        <el-tabs v-model="activeTab">
          <!-- 表单信息标签页 -->
          <el-tab-pane :label="$t('CargoMsg.Cargo_FormInfo')" name="formInfo">
            <el-form-item :label="$t('CargoMsg.Cargo_FormName')" prop="name">
              <el-input v-model="form.name" :placeholder="$t('CargoMsg.Cargo_FormNamePlaceholder')" />
            </el-form-item>
            <el-form-item :label="$t('CargoMsg.Cargo_Branch')" prop="branchId">
              <el-select
                v-model="form.branchId"
                :placeholder="$t('CargoMsg.Cargo_BranchPlaceholder')"
                :disabled="!isCreateMode"
              >
                <el-option
                  v-for="item in officeList"
                  :key="item.officeId"
                  :label="getOfficeLabel(item)"
                  :value="item.officeId"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('CargoMsg.Cargo_ApplicationScene')" prop="applicationScene">
              <el-select
                v-model="form.applicationScene"
                :placeholder="$t('CargoMsg.Cargo_ApplicationScenePlaceholder')"
                :disabled="!isCreateMode"
              >
                <el-option
                  v-for="item in applicationSceneOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('CargoMsg.Cargo_BusinessType')" prop="businessType">
              <el-select
                v-model="form.businessType"
                :placeholder="$t('CargoMsg.Cargo_BusinessTypePlaceholder')"
                :disabled="!isCreateMode"
              >
                <el-option
                  v-for="item in businessTypeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('CargoMsg.Cargo_Remarks')" prop="remarks">
              <el-input
                v-model="form.remarks"
                type="textarea"
                :rows="5"
                :placeholder="$t('CargoMsg.Cargo_RemarksPlaceholder')"
              />
            </el-form-item>
          </el-tab-pane>
          <!-- 输入输出配置标签页 - 仅在非创建模式下显示 -->
          <el-tab-pane
            v-if="!isCreateMode"
            :label="$t('CargoMsg.Cargo_InputOutputConfig')"
            name="ioConfig"
          >
            <div class="io-config">
              <!-- 输入配置部分 -->
              <div class="config-section">
                <div class="section-title">{{ $t('CargoMsg.Cargo_FormInputConfigDescription') }}</div>
                <div class="api-input-group">
                  <span class="api-label">{{ $t('CargoMsg.Cargo_FormInputAPI') }}</span>
                  <el-input
                    v-model="form.inputApi"
                    type="textarea"
                    :rows="4"
                    :placeholder="$t('CargoMsg.Cargo_FormInputAPIPlaceholder')"
                    class="api-textarea"
                  />
                </div>
              </div>

              <!-- 输出配置部分 -->
              <div class="config-section">
                <div class="section-title">{{ $t('CargoMsg.Cargo_FormOutputConfigDescription') }}</div>
                <div class="api-input-group">
                  <span class="api-label">{{ $t('CargoMsg.Cargo_FormOutputAPI') }}</span>
                  <el-input
                    v-model="form.outputApi"
                    type="textarea"
                    :rows="4"
                    :placeholder="$t('CargoMsg.Cargo_FormOutputAPIPlaceholder')"
                    class="api-textarea"
                  />
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-form>
      <span slot="footer">
        <el-button type="primary" @click="handleConfirm">{{ confirmButtonText }}</el-button>
        <el-button @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  APPLICATION_SCENE,
  BUSINESS_TYPE,
  ENABLED,
  PUBLISHED
} from '@/class/cargo/model/parameters/AiCustomPageConstants'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { getCacheListByKey } from '@/utils/basicDataCache'

export default {
  name: 'AiCustomPageFormConfig',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formConfig: {
      type: Object,
      default: () => ({})
    },
    isCreateMode: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 当前激活的标签页
      activeTab: 'formInfo',
      // 对话框可见性
      dialogVisible: false,
      // 表单数据
      form: {
        ...this.formConfig,
        name: this.formConfig.name || '',
        branchId: this.formConfig.branchId || 1,
        applicationScene: this.formConfig.applicationScene || '',
        businessType: this.formConfig.businessType || '',
        remarks: this.formConfig.remarks || '',
        inputApi: this.formConfig.inputApi || '',
        outputApi: this.formConfig.outputApi || ''
      },
      // 表单验证规则
      rules: {
        name: [
          { required: true, message: this.$t('CargoMsg.Cargo_FormNamePlaceholder'), trigger: 'blur' }
        ],
        branchId: [
          { required: true, message: this.$t('CargoMsg.Cargo_BranchPlaceholder'), trigger: 'change' }
        ],
        applicationScene: [
          { required: true, message: this.$t('CargoMsg.Cargo_ApplicationScenePlaceholder'), trigger: 'change' }
        ],
        businessType: [
          { required: true, message: this.$t('CargoMsg.Cargo_BusinessTypePlaceholder'), trigger: 'change' }
        ]
      },
      // 应用公司选项
      officeList: getCacheListByKey(basicCacheKeys.basic_data_Office),
      // 应用场景选项
      applicationSceneOptions: [
        { value: APPLICATION_SCENE.BUSINESS_SINGLE.value, label: this.$t(APPLICATION_SCENE.BUSINESS_SINGLE.key) },
        { value: APPLICATION_SCENE.BUSINESS_BATCH.value, label: this.$t(APPLICATION_SCENE.BUSINESS_BATCH.key) },
        { value: APPLICATION_SCENE.BPM.value, label: 'BPM' },
        { value: APPLICATION_SCENE.OCR.value, label: 'OCR' },
        { value: APPLICATION_SCENE.API.value, label: this.$t(APPLICATION_SCENE.API.key) }
      ],
      // 业务类型选项
      businessTypeOptions: [
        { value: BUSINESS_TYPE.SEA_EXPORT.value, label: this.$t(BUSINESS_TYPE.SEA_EXPORT.key) },
        { value: BUSINESS_TYPE.AIR_EXPORT.value, label: this.$t(BUSINESS_TYPE.AIR_EXPORT.key) }
      ]
    }
  },
  computed: {
    dialogTitle() {
      return this.isCreateMode ? this.$t('CargoMsg.Cargo_CreateForm') : this.$t('CargoMsg.Cargo_ConfigForm')
    },
    confirmButtonText() {
      return this.$t('common.btn.confirm')
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        // 重置表单和验证状态
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate()
        })
        if (this.formConfig.uuid) {
          this.form = { ...this.formConfig }
        } else {
          this.form = {
            name: '',
            branchId: 1,
            applicationScene: this.formConfig.applicationScene || APPLICATION_SCENE.BUSINESS_SINGLE.value,
            businessType: this.formConfig.businessType || BUSINESS_TYPE.SEA_EXPORT.value,
            enabled: this.formConfig.enabled || ENABLED.ENABLED.value,
            published: this.formConfig.published || PUBLISHED.DRAFT.value,
            pageParts: this.formConfig.pageParts || [],
            formData: this.formConfig.formData || {},
            remarks: this.formConfig.remarks || '',
            inputApi: this.formConfig.inputApi || '',
            outputApi: this.formConfig.outputApi || ''
          }
        }
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    },
    isCreateMode: {
      immediate: true,
      handler(val) {
        console.log('创建模式状态变化:', val, '表单配置:', this.formConfig)
      }
    }
  },
  methods: {
    // 获取公司名称
    getOfficeLabel(item) {
      switch (this.$store.getters.language) {
        case 'zh':
          return item.chineseName
        default:
          return item.englishName
      }
    },
    // 取消按钮处理
    handleCancel() {
      this.dialogVisible = false
      this.$emit('cancel', true)
    },
    // 确认按钮处理
    handleConfirm() {
      // 手动验证表单
      const validateField = (field) => {
        return new Promise((resolve) => {
          this.$refs.form.validateField(field, (errorMessage) => {
            resolve(!errorMessage)
          })
        })
      }

      // 验证所有必填字段
      Promise.all([
        validateField('name'),
        validateField('branchId'),
        validateField('applicationScene'),
        validateField('businessType')
      ]).then(([nameValid, branchValid, sceneValid, typeValid]) => {
        if (nameValid && branchValid && sceneValid && typeValid) {
          if (this.isCreateMode) {
            this.$confirm(
              `【${this.$t('CargoMsg.Cargo_Branch')}】【${this.$t('CargoMsg.Cargo_ApplicationScene')}】【${this.$t('CargoMsg.Cargo_BusinessType')}】${this.$t('CargoMsg.Cargo_ConfirmMessageNotEditableAfterCreate')}`,
              this.$t('CargoMsg.Cargo_Tip2'),
              {
                confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
                cancelButtonText: this.$t('common.btn.cancel'),
                type: 'warning'
              }
            ).then(() => {
              this.$emit('confirm', this.form)
              this.dialogVisible = false
            }).catch(() => {})
          } else {
            this.$emit('confirm', this.form)
            this.dialogVisible = false
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.form-config-dialog {
  .el-dialog__header {
    text-align: left;
  }
}

.el-dialog {
  .el-form {
    .el-form-item {
      margin-bottom: 28px !important;
    }
  }
}

.el-form-item__error {
  padding-top: 4px !important;
}

.io-config {
  padding: 16px;

  .config-section {
    margin-bottom: 24px;

    .section-title {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 12px;
      color: #606266;
    }

    .api-input-group {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      .api-label {
        min-width: 80px;
        padding-top: 8px;
        color: #606266;
      }

      .api-textarea {
        flex: 1;
      }
    }
  }
}
</style>
